export type ClassificationSubOption = {
  value: string;
  label: string;
};

export type ClassificationGroup = {
  value: string;
  label: string;
  subOptions: ClassificationSubOption[];
};

export type ClassificationValue = {
  main?: string;
  sub?: string[];
};

export const CLASSIFICATION_OPTIONS: ClassificationGroup[] = [
  {
    value: "software & development",
    label: "Software & Development",
    subOptions: [
      { value: "web development", label: "Web Development" },
      { value: "mobile app development", label: "Mobile App Development" },
      { value: "backend development", label: "Backend Development" },
      { value: "full stack development", label: "Full Stack Development" },
    ],
  },
  {
    value: "infrastructure & cloud",
    label: "Infrastructure & Cloud",
    subOptions: [
      { value: "cloud engineering", label: "Cloud Engineering" },
      {
        value: "devops / site reliability engineering (sre)",
        label: "DevOps / Site Reliability Engineering (SRE)",
      },
      {
        value: "system administration / it infrastructure",
        label: "System Administration / IT Infrastructure",
      },
    ],
  },
  {
    value: "security",
    label: "Security",
    subOptions: [
      { value: "cyber security", label: "Cyber Security" },
      {
        value: "information security / grc (governance, risk, compliance)",
        label: "Information Security / GRC (Governance, Risk, Compliance)",
      },
    ],
  },
  {
    value: "data & ai",
    label: "Data & AI",
    subOptions: [
      { value: "data engineering", label: "Data Engineering" },
      {
        value: "data science & analytics",
        label: "Data Science & Analytics",
      },
      { value: "machine learning / ai", label: "Machine Learning / AI" },
      {
        value: "business intelligence (bi)",
        label: "Business Intelligence (BI)",
      },
    ],
  },
  {
    value: "quality & delivery",
    label: "Quality & Delivery",
    subOptions: [
      { value: "qa / test engineering", label: "QA / Test Engineering" },
      { value: "automation testing", label: "Automation Testing" },
    ],
  },
  {
    value: "product & design",
    label: "Product & Design",
    subOptions: [
      { value: "product management", label: "Product Management" },
      { value: "ui/ux design", label: "UI/UX Design" },
      { value: "graphic / visual design", label: "Graphic / Visual Design" },
    ],
  },
  {
    value: "business & operations",
    label: "Business & Operations",
    subOptions: [
      { value: "business analysis", label: "Business Analysis" },
      {
        value: "project / program management",
        label: "Project / Program Management",
      },
      {
        value: "operations / process management",
        label: "Operations / Process Management",
      },
    ],
  },
  {
    value: "support & services",
    label: "Support & Services",
    subOptions: [
      {
        value: "technical support / helpdesk",
        label: "Technical Support / Helpdesk",
      },
      {
        value: "customer success / implementation",
        label: "Customer Success / Implementation",
      },
    ],
  },
  {
    value: "specialized / emerging fields",
    label: "Specialized / Emerging Fields",
    subOptions: [
      { value: "blockchain / web3", label: "Blockchain / Web3" },
      { value: "ar/vr development", label: "AR/VR Development" },
      { value: "embedded systems / iot", label: "Embedded Systems / IoT" },
      { value: "game development", label: "Game Development" },
      { value: "robotics / automation", label: "Robotics / Automation" },
    ],
  },
  {
    value: "non-tech",
    label: "Non-Tech (Optional, if platform supports)",
    subOptions: [
      {
        value: "sales / business development",
        label: "Sales / Business Development",
      },
      {
        value: "marketing / digital marketing",
        label: "Marketing / Digital Marketing",
      },
      { value: "finance / accounting", label: "Finance / Accounting" },
      { value: "hr / talent acquisition", label: "HR / Talent Acquisition" },
      { value: "legal / compliance", label: "Legal / Compliance" },
    ],
  },
];

const normalize = (value?: string) => value?.trim().toLowerCase();

export const getClassificationGroup = (value?: string) => {
  const normalized = normalize(value);
  if (!normalized) return undefined;
  return CLASSIFICATION_OPTIONS.find((option) => option.value === normalized);
};

export const getClassificationLabel = (value?: string) => {
  const normalized = normalize(value);
  if (!normalized) return "-";

  const group = CLASSIFICATION_OPTIONS.find(
    (option) =>
      option.value === normalized || option.label.toLowerCase() === normalized
  );

  return group?.label || value || "-";
};

export const getClassificationSummary = (classification?: ClassificationValue) => {
  if (!classification?.main) return "-";

  const mainLabel = getClassificationLabel(classification.main);
  const subLabels =
    classification?.sub
      ?.map((sub) => {
        const normalizedSub = normalize(sub);
        const group = getClassificationGroup(classification.main);
        const match = group?.subOptions?.find(
          (option) =>
            option.value === normalizedSub ||
            option.label.toLowerCase() === normalizedSub
        );
        return match?.label || sub;
      })
      .filter(Boolean) || [];

  if (!subLabels.length) return mainLabel;
  return `${mainLabel} - ${subLabels.join(", ")}`;
};

